import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment, { MomentInput } from 'moment-timezone';
import { Observable, catchError, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SharedDialogComponent } from '../components/shared-dialog/shared-dialog.component';
import { SharedWarningModalComponent } from '../components/shared-warning-modal/shared-warning-modal.component';
import {
  CurrentNotificationBanner,
  NotificationBanner,
  NotificationBannerOverlap,
  NotificationBannerRequest
} from '../models/notification-banner.model';
import { TranslationService } from '../translations/services/translation.service';
import { DateFormatService } from './date-format.service';

export function bannerErrorKey(error: unknown, fallbackKey: string): string {
  return error instanceof HttpErrorResponse && error.status === 404 ? 'bannerRecordMissingText' : fallbackKey;
}

@Injectable({ providedIn: 'root' })
export class NotificationBannerService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly translationService = inject(TranslationService);
  private readonly dateFormatService = inject(DateFormatService);
  private readonly adminUrl = `${environment.backOfficeUrl}${environment.serviceUris.notificationBanners}`;
  private readonly currentUrl = `${environment.backOfficeUrl}${environment.serviceUris.currentNotificationBanner}`;

  listBanners(): Observable<NotificationBanner[]> {
    return this.http.get<NotificationBanner[]>(this.adminUrl);
  }

  getBanner(id: string): Observable<NotificationBanner> {
    return this.http.get<NotificationBanner>(`${this.adminUrl}/${id}`);
  }

  /** Emits null when the admin declines (or cannot) replace an overlapping banner. */
  saveBanner(id: string | undefined, request: NotificationBannerRequest): Observable<NotificationBanner | null> {
    return this.withOverlapHandling((replaceConflictingBannerId) => {
      const body = replaceConflictingBannerId ? { ...request, replaceConflictingBannerId } : request;
      return id
        ? this.http.put<NotificationBanner>(`${this.adminUrl}/${id}`, body)
        : this.http.post<NotificationBanner>(this.adminUrl, body);
    });
  }

  activateBanner(id: string): Observable<NotificationBanner | null> {
    return this.withOverlapHandling((replaceConflictingBannerId) =>
      this.http.patch<NotificationBanner>(
        `${this.adminUrl}/${id}/activate`,
        replaceConflictingBannerId ? { replaceConflictingBannerId } : {}
      )
    );
  }

  deactivateBanner(id: string): Observable<NotificationBanner> {
    return this.http.patch<NotificationBanner>(`${this.adminUrl}/${id}/deactivate`, {});
  }

  deleteBanner(id: string): Observable<void> {
    return this.http.delete<void>(`${this.adminUrl}/${id}`);
  }

  /** The backend answers 204 (null body) when no banner is live. */
  getCurrentBanner(): Observable<CurrentNotificationBanner | null> {
    return this.http.get<CurrentNotificationBanner | null>(this.currentUrl);
  }

  formatDateTime(value: MomentInput, utc = false): string {
    const date = utc ? moment.utc(value) : moment(value);
    return date.format(`${this.dateFormatService.currentFormat} h.mm A`);
  }

  private withOverlapHandling(
    write: (replaceConflictingBannerId?: string) => Observable<NotificationBanner>
  ): Observable<NotificationBanner | null> {
    return write().pipe(
      catchError((error: unknown) => {
        const conflicts: NotificationBannerOverlap[] | undefined =
          error instanceof HttpErrorResponse && error.status === 409 ? error.error?.conflictingBanners : undefined;
        if (!conflicts?.length) {
          return throwError(() => error);
        }
        const replaceable = conflicts.length === 1;
        return this.confirmOverlap(conflicts, replaceable).pipe(
          switchMap((confirmed) => (replaceable && confirmed === true ? write(conflicts[0].id) : of(null)))
        );
      })
    );
  }

  private confirmOverlap(conflicts: NotificationBannerOverlap[], replaceable: boolean): Observable<boolean | undefined> {
    const translate = (key: string) => this.translationService.translate(key);
    const overlaps = conflicts
      .map(({ title, startAt, endAt }) => `"${title}" (${this.formatDateTime(startAt)} – ${this.formatDateTime(endAt)})`)
      .join('; ');
    const title = translate('bannerConflictTitleText');
    if (!replaceable) {
      const message = translate('bannerMultipleConflictText').replace('{banners}', overlaps);
      return this.dialog
        .open<SharedWarningModalComponent, SharedWarningModalComponent['data'], boolean>(SharedWarningModalComponent, {
          data: { title, message, buttonText: translate('closeText') }
        })
        .afterClosed();
    }
    return this.dialog
      .open<SharedDialogComponent, SharedDialogComponent['data'], boolean>(SharedDialogComponent, {
        panelClass: 'banner-conflict-dialog',
        data: {
          title,
          message: translate('bannerReplaceConflictText').replace('{banners}', overlaps),
          buttonText: translate('yesText'),
          cancelButtonText: translate('noText')
        }
      })
      .afterClosed();
  }
}
